import * as React from 'react';
import {Suspense} from 'react';
import {graphql, useLazyLoadQuery} from 'react-relay';
import {Link, Navigate, useParams} from 'react-router-dom';
import {Aperture, ArrowLeft, ArrowRight, Brain, Camera, Check, Globe, Layers, Lightbulb, Move3d, Radio, Scan, Zap} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import {SERVICES, serviceBySlug} from '../services';
import {ServicePageGearQuery} from './__generated__/ServicePageGearQuery.graphql';

const GEAR = graphql`
    query ServicePageGearQuery {
        rentalItems(first: 200) {
            nodes { id assetTag name category image dailyRate kitMembers { id } }
        }
    }`;

const Icons = {Scan, Camera, Zap, Globe, Layers, Brain, Radio, Lightbulb, Move3d, Aperture};

// Featured rental items for a service, pulled from the live catalog.
const FeaturedGear: React.FC<{ tags: string[] }> = ({tags}) => {
    const data = useLazyLoadQuery<ServicePageGearQuery>(GEAR, {}, {fetchPolicy: 'store-or-network'});
    const items = (data.rentalItems.nodes ?? []).flatMap((n) => (n && tags.includes(n.assetTag) ? [n] : []));
    if (items.length === 0) return null;
    return (
        <section className="mt-16">
            <h2 className="text-xs uppercase tracking-widest text-gray-500 mb-4">Gear we use for this · also available to rent</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {items.map((item) => (
                    <Link key={item.id} to={`/gear/${item.assetTag}`} className="group bg-[#050505] border border-gray-800 hover:border-lumina-purple/50 p-3 flex flex-col">
                        <div className="aspect-[4/3] bg-gray-900 mb-3 overflow-hidden flex items-center justify-center">
                            {item.image ? <img src={item.image} alt={item.name} className="w-full h-full object-cover"/> : <span className="font-mono text-[10px] text-gray-700">{item.assetTag}</span>}
                        </div>
                        <span className="text-white font-display font-bold text-sm leading-tight group-hover:text-lumina-purple">{item.name}</span>
                        <span className="text-[11px] text-gray-500 mt-1">
                            {item.kitMembers.length > 0 ? `${item.kitMembers.length} pieces · ` : ''}{item.dailyRate ? `$${item.dailyRate}/day` : 'pricing on request'}
                        </span>
                    </Link>
                ))}
            </div>
        </section>
    );
};

const ServicePage: React.FC = () => {
    const {slug = ''} = useParams();
    const service = serviceBySlug(slug);
    if (!service) return <main className="p-12 text-white"><h1>Service not found</h1><Link to="/">Return home</Link></main>;
    const Icon = Icons[service.iconName];
    const others = SERVICES.filter((s) => s.slug !== service.slug);

    return (
        <div className="bg-lumina-dark min-h-screen text-white">
            <Navbar/>
            <main className="max-w-7xl mx-auto px-6 pt-28 pb-24">
                <Link to="/#services" className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-white mb-6"><ArrowLeft className="w-3 h-3"/> All services</Link>

                <div className="grid lg:grid-cols-[1.2fr_1fr] gap-10 items-start">
                    <div>
                        <div className="flex items-center gap-3 mb-3">
                            <span className="p-2 rounded-full bg-lumina-accent/10 border border-lumina-accent/40"><Icon className="w-5 h-5 text-lumina-accent"/></span>
                            <span className="text-lumina-purple font-display text-sm tracking-wider uppercase">Service</span>
                        </div>
                        <h1 className="text-4xl md:text-5xl font-display font-bold leading-tight">{service.title}</h1>
                        <p className="text-xl text-gray-300 mt-3">{service.tagline}</p>
                        <p className="text-gray-400 mt-5 leading-relaxed">{service.summary}</p>

                        <div className="flex flex-wrap gap-3 mt-8">
                            <Link to={`/start?interest=${service.interest}`}
                                  className="inline-flex items-center gap-2 px-6 py-3 bg-lumina-accent text-black text-xs font-bold tracking-widest uppercase hover:bg-white">
                                Start a project <ArrowRight className="w-4 h-4"/>
                            </Link>
                            {service.gearTags.length > 0 && (
                                <Link to="/book" className="inline-flex items-center gap-2 px-6 py-3 border border-gray-700 text-gray-300 text-xs font-bold tracking-widest uppercase hover:border-lumina-accent hover:text-white">
                                    Rent the gear
                                </Link>
                            )}
                        </div>
                    </div>
                    <div className="aspect-[3/2] overflow-hidden border border-gray-800 bg-gray-900">
                        <img src={service.image} alt="" className="w-full h-full object-cover opacity-80"/>
                    </div>
                </div>

                <div className="grid md:grid-cols-3 gap-8 mt-16">
                    <section>
                        <h2 className="text-xs uppercase tracking-widest text-gray-500 mb-3">What we do</h2>
                        <ul className="space-y-2 text-sm text-gray-300">
                            {service.whatWeDo.map((x) => <li key={x} className="flex gap-2"><Check className="w-4 h-4 text-lumina-accent shrink-0 mt-0.5"/>{x}</li>)}
                        </ul>
                    </section>
                    <section>
                        <h2 className="text-xs uppercase tracking-widest text-gray-500 mb-3">What you get</h2>
                        <ul className="space-y-2 text-sm text-gray-300">
                            {service.deliverables.map((x) => <li key={x} className="flex gap-2"><Check className="w-4 h-4 text-lumina-purple shrink-0 mt-0.5"/>{x}</li>)}
                        </ul>
                    </section>
                    <section>
                        <h2 className="text-xs uppercase tracking-widest text-gray-500 mb-3">Good for</h2>
                        <ul className="space-y-2 text-sm text-gray-300">
                            {service.goodFor.map((x) => <li key={x} className="border-l-2 border-gray-800 pl-3">{x}</li>)}
                        </ul>
                    </section>
                </div>

                <section className="mt-16">
                    <h2 className="text-xs uppercase tracking-widest text-gray-500 mb-4">How it goes</h2>
                    <ol className="grid md:grid-cols-4 gap-4">
                        {service.process.map((step, i) => (
                            <li key={step} className="border border-gray-800 p-4">
                                <span className="font-display font-bold text-lumina-accent text-2xl">{i + 1}</span>
                                <p className="text-sm text-gray-300 mt-2">{step}</p>
                            </li>
                        ))}
                    </ol>
                </section>

                {service.gearTags.length > 0 && (
                    <Suspense fallback={null}>
                        <FeaturedGear tags={service.gearTags}/>
                    </Suspense>
                )}

                <section className="mt-16 border-t border-gray-800 pt-10">
                    <h2 className="text-xs uppercase tracking-widest text-gray-500 mb-4">Other services</h2>
                    <div className="flex flex-wrap gap-2">
                        {others.map((s) => (
                            <Link key={s.slug} to={`/services/${s.slug}`} className="text-xs border border-gray-800 px-3 py-2 text-gray-300 hover:border-lumina-accent hover:text-white">{s.title}</Link>
                        ))}
                    </div>
                </section>
            </main>
            <Footer/>
        </div>
    );
};

export default ServicePage;
